import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Card,
  Button,
  Form,
  Input,
  Alert,
  Spin,
  Tag,
  Typography,
  Space,
  Divider,
  Switch,
  Upload,
} from 'antd'
import {
  SettingOutlined,
  TeamOutlined,
  SaveOutlined,
  InfoCircleOutlined,
  CreditCardOutlined,
  PictureOutlined,
  InboxOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import { Show } from '@/components/Show'
import { useAuthStore } from '@/stores/authStore'
import { useConfiguracaoStore } from '@/stores/configuracaoStore'
import { normalizarLogoDoParceiro } from '@/lib/logo-parceiro/normalizar-logo'
import { TAMANHO_MAXIMO_LOGO_EM_BYTES } from '@/lib/logo-parceiro/normalizacao'
import type { BancoDoParceiro } from '@/types'
import efiLogo from '@/assets/efi.png'
import hyperpayLogo from '@/assets/hyperpay.png'

const { Title, Text, Paragraph } = Typography

// ─── Entry component ─────────────────────────────────────────────────────────

export function Configuracoes() {
  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <PageTitle />
      <IntegracaoBancariaCard />
      <CartaoCard />
      <LogoCard />
      <UsuariosCard />
    </div>
  )
}

// ─── Helper components ────────────────────────────────────────────────────────

function PageTitle() {
  return (
    <div>
      <Title level={4} className="!mb-1">
        <SettingOutlined className="mr-2" />
        Configurações
      </Title>
      <Text type="secondary">Gerencie as configurações do seu parceiro.</Text>
    </div>
  )
}

function IntegracaoBancariaCard() {
  const parceiro = useAuthStore((s) => s.parceiro)
  const isAdmin = parceiro?.role === 'admin'
  const { banco, payeeCode, numeroConta, hasContaHyperpay, loading, saving, fetchConfig } =
    useConfiguracaoStore((s) => s)

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  if (loading) {
    return (
      <Card>
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      </Card>
    )
  }

  return (
    <Card
      title={
        <Space>
          <span>Integração bancária</span>
          {!isAdmin && <Tag color="default">Somente leitura</Tag>}
        </Space>
      }
    >
      <Show when={hasContaHyperpay || banco === 'HYPERPAY'}>
        <SeletorDeBanco isAdmin={isAdmin} />
        <Divider />
      </Show>
      {banco === 'HYPERPAY' ? (
        <HyperpayInstructions />
      ) : (
        <>
          <EfiInstructions />
          <VideoDeAjudaEfi />
          <Divider />
          <EfiConfigForm
            initialPayeeCode={payeeCode ?? ''}
            initialNumeroConta={numeroConta ?? ''}
            isAdmin={isAdmin}
            saving={saving}
          />
        </>
      )}
    </Card>
  )
}

const BANCOS = [
  {
    value: 'EFI' as const,
    nome: 'Efí Bank',
    logo: efiLogo,
    descricao: 'Repasse automático na sua conta',
  },
  {
    value: 'HYPERPAY' as const,
    nome: 'Hyperpay/Pilotar',
    logo: hyperpayLogo,
    descricao: 'Cobrança pela Hyperpay, sem configurar conta',
  },
]

function SeletorDeBanco({ isAdmin }: { isAdmin: boolean }) {
  const banco = useConfiguracaoStore((s) => s.banco)
  const savingBanco = useConfiguracaoStore((s) => s.savingBanco)
  const updateBanco = useConfiguracaoStore((s) => s.updateBanco)

  async function handleSelecionar(novoBanco: BancoDoParceiro) {
    if (!isAdmin || novoBanco === banco) return
    try {
      await updateBanco(novoBanco)
      toast.success('Banco alterado com sucesso!')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao alterar o banco')
    }
  }

  return (
    <div>
      <Text strong className="block mb-1">
        Banco emissor das suas cobranças
      </Text>
      <Text type="secondary" className="block mb-3">
        É por ele que a New Driver gera as cobranças das suas matrículas.
      </Text>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {BANCOS.map((opcao) => (
          <CaixaDeBanco
            key={opcao.value}
            opcao={opcao}
            selecionado={banco === opcao.value}
            disabled={!isAdmin || savingBanco}
            onSelecionar={() => handleSelecionar(opcao.value)}
          />
        ))}
      </div>
    </div>
  )
}

function CaixaDeBanco({
  opcao,
  selecionado,
  disabled,
  onSelecionar,
}: {
  opcao: (typeof BANCOS)[number]
  selecionado: boolean
  disabled: boolean
  onSelecionar: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelecionar}
      disabled={disabled}
      className={`flex flex-col items-center justify-center gap-3 rounded-lg border-2 p-5 text-center transition-colors ${
        selecionado ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
      } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-gray-300'}`}
    >
      <img src={opcao.logo} alt={opcao.nome} className="h-8 max-w-[140px] object-contain" />
      <div>
        <Text strong className="block">
          {opcao.nome}
        </Text>
      </div>
      {selecionado && <Tag color="blue">Selecionado</Tag>}
    </button>
  )
}

function HyperpayInstructions() {
  return (
    <Alert
      type="info"
      showIcon
      icon={<InfoCircleOutlined />}
      className="mt-4!"
      description={
        <div className="space-y-2">
          <Paragraph className="!mb-0 font-medium">
            Suas cobranças são emitidas pela Hyperpay/Pilotar.
          </Paragraph>
          <Paragraph className="!mb-0">
            O repasse é liquidado pelo seu CNPJ, então não há código de beneficiário nem número de
            conta a preencher aqui. Você continua definindo quanto cobrar do aluno na tela de
            matrícula.
          </Paragraph>
        </div>
      }
    />
  )
}

function EfiInstructions() {
  return (
    <Alert
      type="info"
      showIcon
      icon={<InfoCircleOutlined />}
      description={
        <div className="space-y-2">
          <Paragraph className="!mb-0 font-medium">
            É necessário possuir uma conta na Efí para receber os repasses.
          </Paragraph>
          <Paragraph className="!mb-0">
            É por ela que o valor da sua parte em cada matrícula cai na sua titularidade, e é o que
            permite emitir PIX e boleto em nome do seu CNPJ. Se você ainda não tem conta, abra uma
            em{' '}
            <a href="https://sejaefi.com.br" target="_blank" rel="noreferrer">
              sejaefi.com.br
            </a>{' '}
            antes de preencher os campos abaixo.
          </Paragraph>
          <Paragraph className="!mb-0">
            <Text strong>Número da Conta:</Text> no portal da Efí, em{' '}
            <Text code>Minha Conta → Dados da Conta</Text>. O número aparece no campo{' '}
            <Text strong>Conta</Text>.
          </Paragraph>
          <Paragraph className="!mb-1">
            <Text strong>Payee Code:</Text> ainda no portal, em{' '}
            <Text code>Cobranças → Pix → Configurações</Text>. É o Código do Beneficiário, listado
            na seção de chaves PIX.
          </Paragraph>
        </div>
      }
    />
  )
}

function VideoDeAjudaEfi() {
  return (
    <div className="mt-4">
      <Paragraph className="!mb-2">
        <Text strong>Vídeo:</Text> onde encontrar o número da conta e o Payee Code no portal da Efí.
      </Paragraph>
      <div className="relative w-full overflow-hidden rounded-lg bg-black pt-[56.25%]">
        <iframe
          src="https://customer-tt8s3c2vz888asa9.cloudflarestream.com/3c9b9e033a04fada5e5a10dd89b2a3ba/iframe"
          title="Como obter o número da conta e o Payee Code na Efí"
          loading="lazy"
          allow="accelerometer; gyroscope; encrypted-media; picture-in-picture;"
          allowFullScreen
          className="absolute inset-0 h-full w-full border-0"
        />
      </div>
    </div>
  )
}

function EfiConfigForm({
  initialPayeeCode,
  initialNumeroConta,
  isAdmin,
  saving,
}: {
  initialPayeeCode: string
  initialNumeroConta: string
  isAdmin: boolean
  saving: boolean
}) {
  const salvarDadosDeRepasse = useConfiguracaoStore((s) => s.salvarDadosDeRepasse)

  const formik = useFormik({
    initialValues: {
      payeeCode: initialPayeeCode,
      numeroConta: initialNumeroConta,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      payeeCode: Yup.string().required('Informe o Payee Code'),
      numeroConta: Yup.string().required('Informe o número da conta'),
    }),
    onSubmit: async (values) => {
      try {
        await salvarDadosDeRepasse(values)
        toast.success('Dados validados na Efí e salvos com sucesso!')
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Erro ao salvar os dados')
      }
    },
  })

  return (
    <Form layout="vertical" onFinish={formik.handleSubmit}>
      <Form.Item
        label="Payee Code"
        validateStatus={formik.touched.payeeCode && formik.errors.payeeCode ? 'error' : ''}
        help={formik.touched.payeeCode && formik.errors.payeeCode}
      >
        <Input
          name="payeeCode"
          value={formik.values.payeeCode}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="Ex: 123456"
          disabled={!isAdmin}
        />
      </Form.Item>

      <Form.Item
        label="Número da Conta"
        validateStatus={formik.touched.numeroConta && formik.errors.numeroConta ? 'error' : ''}
        help={formik.touched.numeroConta && formik.errors.numeroConta}
        className="!mb-0"
      >
        <Input
          name="numeroConta"
          value={formik.values.numeroConta}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="Ex: 100100"
          disabled={!isAdmin}
        />
      </Form.Item>

      {isAdmin && (
        <div className="mt-4">
          <Alert
            type="info"
            showIcon
            className="mb-3"
            description="Ao salvar, geramos um PIX e um boleto de teste de R$ 0,02 com repasse para esta conta, só para confirmar na Efí que os dados estão corretos. O boleto é cancelado em seguida e o PIX expira em minutos — você não precisa pagar nada."
          />
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
            Validar e salvar
          </Button>
        </div>
      )}
    </Form>
  )
}

function CartaoCard() {
  const podeConfigurarNewDriver = useAuthStore((s) => s.podeConfigurarNewDriver)
  const { aceitaCartao, savingCartao, loading, updateAceitaCartao } = useConfiguracaoStore((s) => s)

  if (loading) return null

  async function handleToggle(checked: boolean) {
    try {
      await updateAceitaCartao(checked)
      toast.success(checked ? 'Cartão de crédito ativado!' : 'Cartão de crédito desativado.')
    } catch {
      toast.error('Erro ao salvar configuração de cartão')
    }
  }

  return (
    <Card
      title={
        <Space>
          <CreditCardOutlined />
          <span>Pagamento com Cartão de Crédito</span>
          <TagDeConfiguracaoNewDriver />
        </Space>
      }
      className="mt-4!"
    >
      <Alert
        type="warning"
        showIcon
        className="mb-4"
        description={
          <div className="space-y-1">
            <Paragraph className="!mb-0 font-medium">Atenção antes de ativar:</Paragraph>
            <Paragraph className="!mb-0">
              <Text strong>Prazo de recebimento:</Text> Os valores de pagamentos por cartão ficam
              disponíveis em <Text strong>21 dias úteis</Text> após a confirmação da transação.
            </Paragraph>
            <Paragraph className="!mb-0">
              <Text strong>Tarifas:</Text> As taxas de processamento do cartão são divididas
              proporcionalmente entre todos os beneficiários do repasse. Sua parte da tarifa será
              descontada diretamente do valor que você receber.
            </Paragraph>
          </div>
        }
      />
      <div className="flex items-center gap-3">
        <Switch
          checked={aceitaCartao}
          onChange={handleToggle}
          loading={savingCartao}
          disabled={!podeConfigurarNewDriver}
        />
        <Text>{aceitaCartao ? 'Cartão de crédito ativado' : 'Cartão de crédito desativado'}</Text>
      </div>
    </Card>
  )
}

function TagDeConfiguracaoNewDriver() {
  const podeConfigurarNewDriver = useAuthStore((s) => s.podeConfigurarNewDriver)

  if (podeConfigurarNewDriver) return <Tag color="blue">Somente New Driver</Tag>
  return <Tag color="default">Somente leitura</Tag>
}

const TIPOS_DE_LOGO_ACEITOS = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']

function LogoCard() {
  const isAdministrador = useAuthStore((s) => s.isAdministrador)
  const loading = useConfiguracaoStore((s) => s.loading)

  // Enquanto não se decide se o parceiro pode marcar o próprio certificado, só a equipe New Driver vê.
  if (!isAdministrador) return null
  if (loading) return null

  return (
    <Card
      title={
        <Space>
          <PictureOutlined />
          <span>Logo nos Certificados</span>
          <TagDeConfiguracaoNewDriver />
        </Space>
      }
      className="mt-4!"
    >
      <Alert
        type="info"
        showIcon
        className="mb-4"
        description={
          <div className="space-y-1">
            <Paragraph className="!mb-0">
              A logo é impressa ao lado da logo New Driver nos certificados emitidos para os alunos
              deste parceiro. Sem logo cadastrada, o certificado sai como hoje.
            </Paragraph>
            <Paragraph className="!mb-0">
              <Text strong>Formatos:</Text> PNG, JPG ou WEBP, até 5 MB. A imagem será ajustada
              automaticamente antes do envio. Prefira PNG com fundo transparente — o certificado tem
              fundo claro.
            </Paragraph>
          </div>
        }
      />
      <LogoAtual />
      <LogoDropzone />
    </Card>
  )
}

function LogoAtual() {
  const logoUrl = useConfiguracaoStore((s) => s.logoUrl)
  const savingLogo = useConfiguracaoStore((s) => s.savingLogo)
  const removerLogo = useConfiguracaoStore((s) => s.removerLogo)
  const podeConfigurarNewDriver = useAuthStore((s) => s.podeConfigurarNewDriver)

  if (!logoUrl) return null

  async function handleRemover() {
    try {
      await removerLogo()
      toast.success('Logo removida.')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao remover a logo')
    }
  }

  return (
    <div className="mt-4 flex items-center gap-4">
      <div className="flex h-24 w-40 items-center justify-center rounded border border-gray-200 bg-white p-2">
        <img
          src={logoUrl}
          alt="Logo do parceiro"
          className="max-h-full max-w-full object-contain"
        />
      </div>
      {podeConfigurarNewDriver && (
        <Button danger icon={<DeleteOutlined />} onClick={handleRemover} loading={savingLogo}>
          Remover logo
        </Button>
      )}
    </div>
  )
}

function LogoDropzone() {
  const logoUrl = useConfiguracaoStore((s) => s.logoUrl)
  const savingLogo = useConfiguracaoStore((s) => s.savingLogo)
  const uploadLogo = useConfiguracaoStore((s) => s.uploadLogo)
  const [normalizandoLogo, setNormalizandoLogo] = useState(false)
  const processandoLogo = normalizandoLogo || savingLogo
  const podeConfigurarNewDriver = useAuthStore((s) => s.podeConfigurarNewDriver)

  if (!podeConfigurarNewDriver) return null

  async function handleArquivo(file: File) {
    if (processandoLogo) return
    const erro = validarArquivoDeLogo(file)
    if (erro) {
      toast.error(erro)
      return
    }

    setNormalizandoLogo(true)
    try {
      const logoNormalizada = await normalizarLogoDoParceiro(file)
      await uploadLogo(logoNormalizada)
      toast.success('Logo enviada com sucesso!')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao enviar a logo')
    } finally {
      setNormalizandoLogo(false)
    }
  }

  return (
    <div className="mt-4">
      <Upload.Dragger
        accept=".png,.jpg,.jpeg,.webp"
        showUploadList={false}
        disabled={processandoLogo}
        beforeUpload={(file) => {
          handleArquivo(file)
          return false // tratado aqui; o antd não deve tentar subir o arquivo sozinho
        }}
      >
        {processandoLogo ? (
          <p className="py-4">
            <Spin />{' '}
            <Text type="secondary">{normalizandoLogo ? 'Otimizando...' : 'Enviando...'}</Text>
          </p>
        ) : (
          <>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Arraste a imagem aqui ou clique para selecionar</p>
            <p className="ant-upload-hint">
              {logoUrl ? 'O arquivo enviado substitui a logo atual.' : 'PNG, JPG ou WEBP até 5 MB.'}
            </p>
          </>
        )}
      </Upload.Dragger>
    </div>
  )
}

function validarArquivoDeLogo(file: File) {
  if (!TIPOS_DE_LOGO_ACEITOS.includes(file.type)) return 'Envie a logo em PNG, JPG ou WEBP.'
  if (!file.size) return 'A imagem selecionada está vazia.'
  if (file.size > TAMANHO_MAXIMO_LOGO_EM_BYTES) {
    return 'Arquivo muito grande. Envie uma logo de até 5 MB.'
  }
  return null
}

function UsuariosCard() {
  const parceiro = useAuthStore((s) => s.parceiro)
  const isAdmin = parceiro?.role === 'admin'
  const navigate = useNavigate()

  if (!isAdmin) return null

  return (
    <Card
      title={
        <Space>
          <TeamOutlined />
          <span>Usuários do Painel</span>
        </Space>
      }
      className="mt-4!"
    >
      <Paragraph type="secondary">
        Gerencie os usuários que têm acesso ao painel deste parceiro. Você pode adicionar operadores
        e outros administradores, gerar links de acesso e remover usuários.
      </Paragraph>
      <Button
        type="default"
        icon={<TeamOutlined />}
        onClick={() => navigate('/configuracoes/usuarios')}
      >
        Gerenciar Usuários
      </Button>
    </Card>
  )
}
